// frontend/src/components/TenantEmailSenderTab.jsx
//
// Self-serve admin screen for configuring how this tenant's outbound document
// emails are sent (Option C, client-credentials / app-only model).
//
// Flow (matches the reshaped backend):
//   1. Choose provider + mailbox -> Save        (POST /api/tenant-email-sender)
//   2. (If consent not yet granted) open the admin-consent link
//                                               (GET  /admin-consent-url)
//   3. Send test -> on success, status verified  (POST /test)
//   4. Disconnect reverts to no-reply fallback   (POST /disconnect)
//
// There is NO per-config OAuth redirect to capture: app-only sending fetches its
// own token server-side. Admin consent is a one-time tenant act.
//
// SECURITY: this screen never sends a tenant_id (backend binds to the session)
// and never sees token material. secureAxios for all calls; Promise.allSettled
// for the batched read.

import React, { useEffect, useState } from "react";
import secureAxios from "../utils/secureAxios";

// Lifecycle status -> how it renders. Keys match backend status strings.
const STATUS_META = {
  unconfigured: {
    label: "Not configured",
    badge: "bg-secondary",
    help: "Documents currently send from the default no-reply address.",
  },
  pending_setup: {
    label: "Pending setup",
    badge: "bg-warning text-dark",
    help:
      "Mailbox saved. Ensure your administrator has granted consent and the " +
      "mailbox is permitted, then send a test to verify.",
  },
  verified: {
    label: "Verified",
    badge: "bg-success",
    help: "Verified. Documents send from your organization's mailbox.",
  },
};

// Providers wired end-to-end and selectable in this phase.
const PROVIDERS = [
  { value: "microsoft", label: "Microsoft 365 (Outlook / Exchange)" },
];

// Planned but not self-serve here (Google = Workspace-admin setup, deferred).
const PROVIDERS_COMING_SOON = [
  { value: "google", label: "Google Workspace (Gmail)" },
];

const ALL_PROVIDER_LABELS = [...PROVIDERS, ...PROVIDERS_COMING_SOON].reduce(
  (acc, p) => ({ ...acc, [p.value]: p.label }),
  {}
);

const TenantEmailSenderTab = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null); // { type, text }
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const [provider, setProvider] = useState("microsoft");
  const [mailbox, setMailbox] = useState("");

  const loadConfig = async () => {
    try {
      setLoading(true);
      const [configRes, profileRes] = await Promise.allSettled([
        secureAxios.get("/api/tenant-email-sender"),
        secureAxios.get("/api/auth/profile"),
      ]);

      if (
        profileRes.status === "fulfilled" &&
        !profileRes.value.data?.is_admin
      ) {
        setError("You must be an administrator to access this page.");
        setLoading(false);
        return;
      }

      if (configRes.status === "fulfilled") {
        setConfig(configRes.value.data);
        if (configRes.value.data?.send_as_mailbox) {
          setMailbox(configRes.value.data.send_as_mailbox);
        }
        if (configRes.value.data?.provider) {
          setProvider(configRes.value.data.provider);
        }
        setError(null);
      } else {
        console.error("Failed to load sender config:", configRes.reason);
        setError("Error loading email sender configuration.");
      }
    } catch (err) {
      console.error("Unexpected error loading sender config:", err);
      setError("Error loading email sender configuration.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleSave = async () => {
    if (!mailbox.trim()) {
      setMessage({ type: "danger", text: "Sending mailbox is required." });
      return;
    }
    try {
      setSaving(true);
      await secureAxios.post("/api/tenant-email-sender", {
        provider,
        send_as_mailbox: mailbox.trim(),
      });
      setMessage({ type: "success", text: "Saved. Now grant consent (if needed) and send a test." });
      await loadConfig();
    } catch (err) {
      const status = err.response?.status;
      if (status === 503) {
        setMessage({
          type: "danger",
          text: "Email provider isn't set up in this environment yet.",
        });
      } else {
        console.error("save failed:", err);
        setMessage({ type: "danger", text: "Failed to save configuration." });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAdminConsent = async () => {
    try {
      const res = await secureAxios.get("/api/tenant-email-sender/admin-consent-url");
      if (res.data?.adminConsentUrl) {
        // Open the one-time admin-consent page in a new tab.
        window.open(res.data.adminConsentUrl, "_blank", "noopener");
      }
    } catch (err) {
      console.error("admin consent url failed:", err);
      setMessage({ type: "danger", text: "Could not build the consent link. Save a mailbox first." });
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      const res = await secureAxios.post("/api/tenant-email-sender/test", {});
      setMessage({
        type: "success",
        text: `Test email sent to ${res.data?.sent_to || "your address"}. Status is now verified.`,
      });
      await loadConfig();
    } catch (err) {
      const text =
        err.response?.data?.error ||
        "Test send failed. Check consent and the mailbox permission.";
      setMessage({ type: "danger", text });
    } finally {
      setTesting(false);
    }
  };

  const handleDisconnect = async () => {
    if (
      !window.confirm(
        "Disconnect email sending? Documents will revert to the default " +
          "no-reply address until reconfigured."
      )
    ) {
      return;
    }
    try {
      await secureAxios.post("/api/tenant-email-sender/disconnect", {});
      setMessage({ type: "success", text: "Disconnected. Reverted to no-reply." });
      await loadConfig();
    } catch (err) {
      console.error("disconnect failed:", err);
      setMessage({ type: "danger", text: "Failed to disconnect." });
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading email sender configuration...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <i className="bi bi-exclamation-triangle-fill me-2"></i>
        {error}
      </div>
    );
  }

  const status = config?.status || "unconfigured";
  const meta = STATUS_META[status] || STATUS_META.unconfigured;
  const isConfigured = status !== "unconfigured";
  const canTest = isConfigured && !!mailbox.trim();

  return (
    <>
      <h5 className="mb-3">
        <i className="bi bi-envelope-at me-2"></i>
        Document Email Sender
      </h5>

      <p className="text-muted">
        Configure document emails to send from your organization's own mailbox
        instead of the default no-reply address. Replies always go to the
        sending staff member.
      </p>

      {message && (
        <div className={`alert alert-dismissible fade show alert-${message.type}`}>
          {message.text}
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Current status */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <span className="me-2">Status:</span>
              <span className={`badge ${meta.badge}`}>{meta.label}</span>
            </div>
            {isConfigured && (
              <button
                className="btn btn-outline-danger btn-sm"
                onClick={handleDisconnect}
              >
                <i className="bi bi-x-circle me-1"></i>
                Disconnect
              </button>
            )}
          </div>
          <div className="form-text mt-2">{meta.help}</div>

          {isConfigured && (
            <dl className="row mb-0 mt-3">
              <dt className="col-sm-3">Provider</dt>
              <dd className="col-sm-9">
                {ALL_PROVIDER_LABELS[config.provider] || config.provider}
              </dd>
              <dt className="col-sm-3">Sending mailbox</dt>
              <dd className="col-sm-9">{config.send_as_mailbox || "-"}</dd>
            </dl>
          )}
        </div>
      </div>

      {/* Setup */}
      <div className="card">
        <div className="card-body">
          <h6 className="mb-3">{isConfigured ? "Update configuration" : "Set up sending"}</h6>

          <div className="mb-3">
            <label className="form-label">Email provider</label>
            <select
              className="form-select"
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
            >
              {PROVIDERS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
            <div className="form-text">Choose the email service your organization uses.</div>
            {PROVIDERS_COMING_SOON.length > 0 && (
              <div className="alert alert-secondary mt-2 mb-0 py-2 px-3 small">
                <i className="bi bi-info-circle me-1"></i>
                Google Workspace support is coming. It requires a one-time setup by
                your organization's Google Workspace administrator and isn't
                configured from this screen.
              </div>
            )}
          </div>

          <div className="mb-3">
            <label className="form-label">Sending mailbox</label>
            <input
              className="form-control"
              type="email"
              value={mailbox}
              onChange={(e) => setMailbox(e.target.value)}
              placeholder="documents@yourdomain.com"
            />
            <div className="form-text">
              A shared/service mailbox is recommended (for example
              documents@yourdomain.com) so sending isn't tied to one person.
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2">
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Saving...</span>
                  </span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="bi bi-save me-2"></i>
                  Save
                </>
              )}
            </button>

            <button
              className="btn btn-outline-secondary"
              onClick={handleAdminConsent}
              disabled={!isConfigured}
              title="Opens the one-time administrator consent page"
            >
              <i className="bi bi-shield-lock me-2"></i>
              Grant admin consent
            </button>

            <button
              className="btn btn-success"
              onClick={handleTest}
              disabled={!canTest || testing}
            >
              {testing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Sending...</span>
                  </span>
                  Sending test...
                </>
              ) : (
                <>
                  <i className="bi bi-send-check me-2"></i>
                  Send test email
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default TenantEmailSenderTab;